import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { API_URL } from "../constants/bank";
// The login lives in a cookie the API sets, shared by every app, so send it
// along. The server refuses cookie-only changes without X-Requested-With.
const API = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});
API.interceptors.request.use((req) => {
  // Logins from before the shared cookie kept the token in storage
  const token = JSON.parse(secureLocalStorage.getItem("profile"))?.token;
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// ---- Ending the session ----------------------------------------------------
// When an admin logs this user out or suspends them (or the login expires or
// the password changes), the server answers with 401 + code SESSION_ENDED.
// Other 401s (wrong code, action not allowed...) are left to the page.
const LOGIN_PATH = "/admin/login";
let endingSession = false;

const endSession = () => {
  if (endingSession || window.location.pathname === LOGIN_PATH) return;
  endingSession = true;
  secureLocalStorage.clear();
  window.alert("Your session has ended. Please log in again.");
  // A full page load also resets the app's in-memory state
  window.location.assign(LOGIN_PATH);
};

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      error.response?.data?.code === "SESSION_ENDED" &&
      secureLocalStorage.getItem("profile")
    ) {
      endSession();
    }
    return Promise.reject(error);
  }
);

// Also check every minute and when the user returns to the tab, so someone
// who is just looking at a page is logged out too.
const SESSION_CHECK_MS = 60 * 1000;

const checkSession = () => {
  if (
    document.visibilityState !== "visible" ||
    !secureLocalStorage.getItem("profile")
  ) {
    return;
  }
  API.get("/api/v1/users/session").catch(() => {});
};

if (!window.__sessionCheckStarted) {
  window.__sessionCheckStarted = true;
  setInterval(checkSession, SESSION_CHECK_MS);
  document.addEventListener("visibilitychange", checkSession);
}

const user = JSON.parse(secureLocalStorage.getItem("profile"));
const check = user
  ? `${
      user.results.branch === "head office"
        ? ""
        : "?location[eq]=" + user.results.branch
    }`
  : "";
const check2 = user
  ? `?status[eq]=pending&location[eq]=${user.results.branch}`
  : "";

export const fetchLocations = () => API.get("/api/v1/company-location");

// ! Assets

// sign in a new user
export const signIn = (formData) => API.post("/api/v1/users/login", formData);
// the user already signed in to another app, if any (shared login cookie)
export const fetchSignedInUser = () => API.get("/api/v1/users/me");
// ends the shared login for every app in this browser
export const signOut = () => API.post("/api/v1/users/logout");
// Sign up a new user
export const signup = (formData) => API.post("/api/v1/users/signup", formData);

// Security
// forgot password
export const forgotPassword = (postData) =>
  API.post("/api/v1/users/forgotPassword", postData);

// reset password
export const resetPassword = (token, postData) =>
  API.patch(`/api/v1/users/resetPassword/${token}`, postData);

// reset password
export const changePassword = (postData) =>
  API.patch(`/api/v1/users/updateMypassword`, postData);

export const getUsers = () => API.get("/api/v1/users");

// ! Update users information and delete users
export const updateUser = (id, postData) =>
  API.patch(`/api/v1/users/update-user/info/${id}`, postData);
export const adminResetPassword = (id, password) =>
  API.patch(`/api/v1/users/admin-reset-password/${id}`, { password });
export const deleteUser = (id) =>
  API.delete(`/api/v1/users/delete-selected-user/info/${id}`);

// Authenticator-app sign-in: the switch for every app, and removing a user's
// app when they lose their phone
export const getTwoFactorSettings = () =>
  API.get("/api/v1/users/two-factor/settings");
export const updateTwoFactorSettings = (postData) =>
  API.patch("/api/v1/users/two-factor/settings", postData);
export const resetUserTwoFactor = (id) =>
  API.patch(`/api/v1/users/two-factor/reset/${id}`);

// STOCK

export const getStock = () => API.get("/api/v1/stock");
export const createStock = (postData) => API.post("/api/v1/stock", postData);

// depletion
export const depleteStock = (item, quantity, location) =>
  API.get(`/api/v1/stock/${item}/${quantity}/${location}`);

// stock items

export const getStockItems = () => API.get("/api/v1/stock-item");
export const createStockItems = (postData) =>
  API.post("/api/v1/stock-item", postData);

export const deleteStockItem = (id) => API.delete(`/api/v1/stock-item/${id}`);
export const updateStockItem = (id, postData) =>
  API.patch(`/api/v1/stock-item/${id}`, postData);

// Receipts

export const createReceipt = (postData) =>
  API.post("/api/v1/stock-receipt", postData);

export const getReceipt = () => API.get(`/api/v1/stock-receipt${check}`);
// branch receipt
// export const getBranchReceipt = () =>
//   API.get(`/api/v1/stock-receipt?location[eq]=${branch}`);

// history
export const getStockHistoryDefault = () =>
  API.get("/api/v1/stock-history/stock-history-default");
export const getStockHistoryWithDate = (start, end) =>
  API.get(`/api/v1/stock-history/stock-history/${start}/${end}`);

// location
export const createLocation = (postData) =>
  API.post("/api/v1/stock-location", postData);
export const getStockLocation = () => API.get("/api/v1/stock-location");

export const deleteStockLocation = (id) =>
  API.delete(`/api/v1/stock-location/${id}`);

// Request

export const getRequest = () => API.get(`/api/v1/stock-request${check}`);
export const createRequest = (postData) =>
  API.post(`/api/v1/stock-request`, postData);
export const deleteRequest = (id) => API.delete(`/api/v1/stock-request/${id}`);
export const updateRequest = (id, postData) =>
  API.patch(`/api/v1/stock-request/${id}`, postData);

export const getLocationsRequest = () =>
  API.get("/api/v1/stock-request/request/incoming");

export const locationReceived = () => API.get(`/api/v1/stock-receipt${check2}`);
export const updateReceipt = (id, postData) =>
  API.patch(`/api/v1/stock-receipt/${id}`, postData);

// Authenticator-app sign-in: the code step after a correct password
export const twoFactorLoginVerify = (postData) =>
  API.post("/api/v1/users/two-factor/login/verify", postData);
export const twoFactorLoginSetup = (postData) =>
  API.post("/api/v1/users/two-factor/login/setup", postData);
export const twoFactorLoginSetupConfirm = (postData) =>
  API.post("/api/v1/users/two-factor/login/setup/confirm", postData);
// ...and the signed-in user's own authenticator app
export const getMyTwoFactor = () => API.get("/api/v1/users/two-factor/me");
export const startMyTwoFactorSetup = () =>
  API.post("/api/v1/users/two-factor/me/setup");
export const enableMyTwoFactor = (code) =>
  API.post("/api/v1/users/two-factor/me/enable", { code });
export const disableMyTwoFactor = (postData) =>
  API.post("/api/v1/users/two-factor/me/disable", postData);
export const newMyBackupCodes = (code) =>
  API.post("/api/v1/users/two-factor/me/backup-codes", { code });
